// The maths keyboard layouts for practice answers, one per kind of answer
// (spec: Brain elite-scholars/30-projects/practice-solving-ux-spec.md, section
// 3). Copied from the packs' keyboards.json (60-outputs/practice/maths-p1/,
// and maths-p2/ for 'trig' and 'general').
//
// Client-safe: no content, no answers. Each question names its layout in
// `input.keyboard`; the VAR slot is replaced by the question's first key
// (x, n, k, p...). The page sets
//   mathVirtualKeyboard.layouts = [ thatLayout, 'numeric', 'symbols', 'alphabetic' ]
// so the question's own keys come first and the full keyboard is a tab away.

export const KEYBOARD_NAMES = [
  'numbers',
  'inequality',
  'solutions',
  'points',
  'expression',
  'money',
  'ratio',
  'degrees',
  // Paper 2 (Mac, 12 October 2026): answers with sin/cos/tan, and trig
  // general solutions (x = 30° + k · 360° or ...).
  'trig',
  'general'
] as const;

export type KeyboardName = typeof KEYBOARD_NAMES[number];

/** A MathLive keycap: a plain string (typed as LaTeX or a [command]) or an object. */
export type Keycap = string | {
  latex: string
  label?: string
  aside?: string
};

export const KEYCAPS: Record<string, Exclude<Keycap, string>> = {
  frac: {
    latex: '\\frac{#@}{#?}',
    aside: 'fraction'
  },
  pow: {
    latex: '#@^{#?}',
    aside: 'power'
  },
  sqrt: {
    latex: '\\sqrt{#0}',
    aside: 'root'
  },
  comma: {
    latex: '{,}',
    label: ',',
    aside: 'decimal'
  },
  or: {
    latex: '\\text{ or }',
    label: 'or'
  },
  and: {
    latex: '\\text{ and }',
    label: 'and'
  },
  deg: {
    latex: '^{\\circ}',
    label: '°'
  },
  log: {
    latex: '\\log_{#?}(#0)',
    label: 'log'
  },
  sin: {
    latex: '\\sin',
    label: 'sin'
  },
  cos: {
    latex: '\\cos',
    label: 'cos'
  },
  tan: {
    latex: '\\tan',
    label: 'tan'
  },
  cdot: {
    latex: '\\cdot',
    label: '·',
    aside: 'times'
  }
};

export const KEYBOARD_ROWS: Record<KeyboardName, string[][]> = {
  numbers: [ [ '7', '8', '9', 'frac' ], [ '4', '5', '6', 'sqrt' ], [ '1', '2', '3', '[left]' ], [ '0', 'comma', '-', '[right]', '[backspace]' ] ],
  inequality: [ [ '7', '8', '9', 'VAR', '<', '\\le' ], [ '4', '5', '6', '>', '\\ge', 'or' ], [ '1', '2', '3', 'frac', 'sqrt', '[left]' ], [ '0', 'comma', '-', '[right]', '[backspace]' ] ],
  solutions: [ [ '7', '8', '9', 'VAR', '=' ], [ '4', '5', '6', 'or', 'frac' ], [ '1', '2', '3', 'sqrt', '[left]' ], [ '0', 'comma', '-', '[right]', '[backspace]' ] ],
  points: [ [ '7', '8', '9', '(', ')' ], [ '4', '5', '6', ';', 'and' ], [ '1', '2', '3', 'frac', '[left]' ], [ '0', 'comma', '-', '[right]', '[backspace]' ] ],
  expression: [ [ '7', '8', '9', 'VAR', 'pow', '(' ], [ '4', '5', '6', 'frac', 'sqrt', ')' ], [ '1', '2', '3', '+', '\\times', '[left]' ], [ '0', 'comma', '-', '[right]', '[backspace]' ] ],
  money: [ [ '7', '8', '9', '[backspace]' ], [ '4', '5', '6', '[left]' ], [ '1', '2', '3', '[right]' ], [ '0', 'comma', '-' ] ],
  ratio: [ [ '7', '8', '9', 'frac' ], [ '4', '5', '6', 'sqrt' ], [ '1', '2', '3', '[left]' ], [ '0', 'comma', '-', '[right]', '[backspace]' ] ],
  degrees: [ [ '7', '8', '9', 'deg' ], [ '4', '5', '6', '[left]' ], [ '1', '2', '3', '[right]' ], [ '0', 'comma', '-', '[backspace]' ] ],
  trig: [ [ '7', '8', '9', 'VAR', 'sin', 'cos' ], [ '4', '5', '6', 'frac', 'tan', 'pow' ], [ '1', '2', '3', '+', 'sqrt', '[left]' ], [ '0', 'comma', '-', '(', ')', '[right]', '[backspace]' ] ],
  general: [ [ '7', '8', '9', 'VAR', '=', 'deg' ], [ '4', '5', '6', '+', 'cdot', 'k' ], [ '1', '2', '3', 'or', '-', '[left]' ], [ '0', 'comma', '[right]', '[backspace]' ] ]
};

const LABELS: Record<KeyboardName, string> = {
  numbers: 'Numbers',
  inequality: 'Inequality',
  solutions: 'Solutions',
  points: 'Points',
  expression: 'Expression',
  money: 'Amount',
  ratio: 'Ratio',
  degrees: 'Angle',
  trig: 'Trig',
  general: 'General solution'
};

export function isKeyboardName(name: string): name is KeyboardName {

  return (KEYBOARD_NAMES as readonly string[]).includes(name);
}

/** One key as MathLive takes it; a key name from KEYCAPS, else the literal. */
function keycap(key: string): Keycap {

  return KEYCAPS[key] ?? key;
}

/**
 * The layout for MathLive's mathVirtualKeyboard.layouts. VAR becomes the
 * question's first key (a variable, or a keycap name such as 'log'); when the
 * question names no key, the slot is dropped.
 */
export function keyboardLayout(name: KeyboardName, keys: readonly string[] = []): {
  label: string
  rows: Keycap[][]
} {

  const variable = keys[0];
  const extra = keys.slice(1).map(keycap);

  const rows = KEYBOARD_ROWS[name].map((row) => row.flatMap((key): Keycap[] => {

    if (key === 'VAR') {

      return variable ? [ keycap(variable) ] : [];
    }

    return [ keycap(key) ];
  }));

  // Further keys (func-03 has [ 'x', 'log' ]) go on the top row.
  if (extra.length > 0 && rows[0]) {

    rows[0] = [ ...rows[0], ...extra ];
  }

  return {
    label: LABELS[name],
    rows
  };
}
